import { ApplicationMessageSource } from '../../core/models/application-message-source';
import { MessageType } from '../../core/models/enum/message-type';
import { ApplicationMessage } from '../../core/models/application-message';
import { Patterns } from '../../shared/validation/patterns';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import {
  IPromise,
  IValidator,
  MessageLevel,
  Validator,
} from '../../shared/validation/validators';
import { FieldState } from '../../shared/models/field-state';
import { Utils } from '../../shared/utils';
import { CustomMessageService } from '../../core/message.service';
import { CustomerService } from '../shared/services/customer.service';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import { CustomerModel } from '../shared/models/customer-model';
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-customer-edit-billing',
  templateUrl: './customer-edit-billing.component.html',
  styleUrls: ['./customer-edit-billing.component.less'],
  standalone: false,
})
export class CustomerEditBillingComponent implements OnInit {
  @Output()
  public onUpdate: EventEmitter<CustomerModel> =
    new EventEmitter<CustomerModel>();

  @Input('customer')
  public customerInput: CustomerModel;

  public customer: CustomerModel;

  private initialIdNumber: string;

  @Input()
  public vatStatuses: CommonLookupItem<number, string>[];

  public fieldStates: {
    legalName: FieldState;
    legalAddress: FieldState;
    idNumber: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private customerService: CustomerService,
    private messageService: CustomMessageService,
    private translationService: TranslateService
  ) {
    this.customer = new CustomerModel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerInput'].currentValue != null) {
      this.customer = Utils.cloneObject(this.customerInput, CustomerModel);
      this.initialIdNumber = this.customerInput && this.customerInput.idNumber;
      this.initializeFieldsState();
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      legalName: new FieldState(() => this.customer && this.customer.legalName),
      legalAddress: new FieldState(
        () => this.customer && this.customer.legalAddress
      ),
      idNumber: new FieldState(() => this.customer && this.customer.idNumber),
    };
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  public onSaveClick() {
    let customer = this.customer;
    let varlidators = this.getValidators();
    this.invokeValidation(varlidators, () => {
      this.customerService.saveCustomerBilling(customer).subscribe(() => {
        this.messageService.pushMessage(<ApplicationMessage>{
          type: MessageType.Success,
          sourceId: ApplicationMessageSource.global,
          summary: '',
          detail: this.translationService.instant('message.SaveMessage'),
        });
        this.initializeFieldsState();
        this.onUpdate.emit(customer);
      });
    });
  }

  public onCancelClick() {
    this.customer = Utils.cloneObject(this.customerInput, CustomerModel);
    this.initializeFieldsState();
  }

  ngOnInit() {}

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private getValidators() {
    let field = this.field;
    let customer = this.customer;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let isAlreadyInUse = this.translationService.instant(
      'message.IsAlreadyInUse'
    );

    return [
      Validator.forRequired(
        field.idNumber,
        () => customer.idNumber,
        emptyMessage
      ),
      Validator.forPattern(
        field.idNumber,
        () => customer.idNumber,
        Patterns.taxId,
        incorrectFormat
      ),
      this.initialIdNumber != (this.customer && this.customer.idNumber)
        ? Validator.forAsync(
            field.idNumber,
            () => {
              var p = this.customerService.validateIdNumber(this.customer);
              return <IPromise>{
                catch: (error) => p.catch(error),
                then: (data) => p.then(data),
                finally: () => {
                  alert('todo');
                },
              };
            },
            (response: any) => {
              var isValid = response.isSuccess;
              return {
                message: isValid ? null : isAlreadyInUse,
                level: isValid ? null : MessageLevel.Error,
                isValid: isValid,
              };
            }
          )
        : Validator.forBypass(field.idNumber),
      Validator.forNotRequired(field.legalName, () => customer.legalName),
      Validator.forPattern(
        field.legalName,
        () => customer.legalName,
        Patterns.legalName,
        incorrectFormat
      ),
      Validator.forNotRequired(field.legalAddress, () => customer.legalAddress),
      Validator.forPattern(
        field.legalAddress,
        () => customer.legalAddress,
        Patterns.address,
        incorrectFormat
      ),
    ];
  }
}
